import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBlogPosts, getBlogPost, processBlogContent } from '../utils/blogService';
import PublicLayout from '../components/PublicLayout';
import '../styles/blog-quantumblack.css';
import blogHeaderImage from '../assets/BlueWave-small.jpeg';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [currentMessage, setCurrentMessage] = useState(0);
  const { postId } = useParams();
  const navigate = useNavigate();

  const categories = [
    'All', 
    'Data Preparation', 
    'Conversational Agents', 
    'Predictive Analytics', 
    'Prescriptive AI', 
    'Recommendation Systems', 
    'Computer Vision', 
    'Natural Language Processing', 
    'Custom AI Solutions'
  ];
  
  const rotatingMessages = [
    {
      title: "Data-Driven Decisions",
      subtitle: "Transform raw information into strategic advantage"
    },
    {
      title: "AI-Powered Automation", 
      subtitle: "Streamline operations with intelligent systems"
    },
    {
      title: "Predictive Intelligence",
      subtitle: "Anticipate trends before they happen"
    },
    {
      title: "Connected Insights",
      subtitle: "Unify your data ecosystem for maximum impact"
    }
  ];

  useEffect(() => {
    loadBlogPosts();
  }, []);

  useEffect(() => {
    if (postId && posts.length > 0) {
      loadBlogPost(postId);
    }
  }, [postId, posts]);

  const loadBlogPosts = async () => {
    try {
      const blogPosts = await getBlogPosts();
      // Ensure all posts have required fields
      const normalizedPosts = blogPosts.map(post => ({
        ...post,
        date: post.date || post.lastModified || new Date().toISOString(),
        category: post.category || '',
        author: post.author || 'Fox River AI',
        image: post.image || null
      }));
      setPosts(normalizedPosts);
      setFilteredPosts(normalizedPosts);
    } catch (error) {
      console.error('Error loading blog posts:', error);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    console.log('Filtering posts. Active filter:', activeFilter, 'Posts:', posts);
    let filtered = posts;
    
    // Show all posts when "All" is selected, regardless of category
    if (activeFilter !== 'All') {
      console.log('Filtering by category:', activeFilter);
      filtered = filtered.filter(post => {
        console.log('Post category:', post.category, 'matches filter:', post.category === activeFilter);
        return post.category === activeFilter;
      });
    }
    
    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    console.log('Filtered posts:', filtered);
    setFilteredPosts(filtered);
  }, [posts, activeFilter, searchTerm]);

  // Rotate messages every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % rotatingMessages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [rotatingMessages.length]);

  const loadBlogPost = async (id) => {
    try {
      const post = await getBlogPost(id);
      if (post) {
        const processedContent = await processBlogContent(post.content);
        setSelectedPost({ ...post, processedContent });
      }
    } catch (error) {
      console.error('Error loading blog post:', error);
    }
  };

  const handlePostClick = (post) => {
    navigate(`/blog/${post.id}`);
  };

  const handleBackToList = () => {
    navigate('/blog');
    setSelectedPost(null);
  };

  if (loading) {
    return (
      <div className="blog-container">
        <div className="loading">Loading blog posts...</div>
      </div>
    );
  }

  return (
    <PublicLayout>
      <div className="blog-container">
      <header className="blog-header">
        <img
          className="blog-header-background"
          src={blogHeaderImage}
          alt="AI Insights"
          onError={(e) => {
            console.error('Blog header image failed to load:', blogHeaderImage);
            e.currentTarget.style.display = 'none';
          }}
        />
        <div className="blog-header-overlay"></div>
        <div className="blog-header-content">
          <div className="rotating-message">
            <h1 key={currentMessage} className="message-title">
              {rotatingMessages[currentMessage].title}
            </h1>
            <p key={`${currentMessage}-subtitle`} className="message-subtitle">
              {rotatingMessages[currentMessage].subtitle}
            </p>
          </div>
          <div className="static-message">
            <p>Discover insights that drive transformation</p>
          </div>
        </div>
      </header>

      {selectedPost ? (
        <article className="blog-post-detail">
          <button onClick={handleBackToList} className="back-button">
            ← Back to Blog
          </button>
          <header className="post-header">
            <h1>{selectedPost.title}</h1>
            <div className="post-meta">
              <span className="post-date">
                {new Date(selectedPost.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              {selectedPost.author && <span className="post-author">by {selectedPost.author}</span>}
            </div>
          </header>
          <div 
            className="post-content"
            dangerouslySetInnerHTML={{ __html: selectedPost.processedContent }}
          />
        </article>
      ) : (
        <>
          <div className="blog-filters">
            <div className="filters-container">
              <div className="category-dropdown">
                <label htmlFor="category-select">Category:</label>
                <select 
                  id="category-select"
                  value={activeFilter} 
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="category-select"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search insights..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span>🔍</span>
              </div>
            </div>
          </div>
          
          <div className="blog-posts-list">
            {filteredPosts.length === 0 ? (
              <div className="no-posts">
                <p>{posts.length === 0 ? 'No blog posts available yet. Check back soon!' : 'No posts match your search criteria.'}</p>
              </div>
            ) : (
              <div className="posts-grid">
                {filteredPosts.map((post) => (
                  <article key={post.id} className="blog-post-preview" onClick={() => handlePostClick(post)}>
                    <div className="post-image">
                      {post.category && <div className="post-category">{post.category}</div>}
                      <img 
                        src={post.image || `data:image/svg+xml;base64,${btoa(`<svg width="400" height="200" xmlns="http://www.w3.org/2000/svg"><rect width="400" height="200" fill="#1e3a8a"/><text x="200" y="100" font-family="Arial" font-size="20" fill="white" text-anchor="middle" dominant-baseline="middle">Fox River AI</text></svg>`)}`} 
                        alt={post.title} 
                        onError={(e) => {
                          e.target.src = `data:image/svg+xml;base64,${btoa(`<svg width="400" height="200" xmlns="http://www.w3.org/2000/svg"><rect width="400" height="200" fill="#1e3a8a"/><text x="200" y="100" font-family="Arial" font-size="20" fill="white" text-anchor="middle" dominant-baseline="middle">Fox River AI</text></svg>`)}`;
                        }}
                      />
                    </div>
                    <div className="post-content">
                      <h2>{post.title}</h2>
                      <div className="post-meta">
                        <span className="post-date">
                          📅 {post.date ? new Date(post.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'No date'}
                        </span>
                        {post.author && <span className="post-author">👤 {post.author}</span>}
                      </div>
                      {post.excerpt && <p className="post-excerpt">{post.excerpt}</p>}
                      <div className="read-more">Read More</div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      </div>
    </PublicLayout>
  );
};

export default BlogPage;
